import React from 'react';

interface UnifiedMetricCardProps {
  title: string;
  value: string | number;
  unit?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: number;
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'indigo';
  icon?: React.ReactNode;
  loading?: boolean;
  onClick?: () => void;
}

export const UnifiedMetricCard: React.FC<UnifiedMetricCardProps> = ({
  title,
  value,
  unit,
  trend,
  trendValue,
  color = 'blue',
  icon,
  loading,
  onClick
}) => {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 text-blue-900 dark:text-blue-100',
    green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 text-green-900 dark:text-green-100',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700 text-yellow-900 dark:text-yellow-100',
    red: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 text-red-900 dark:text-red-100',
    purple: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-700 text-purple-900 dark:text-purple-100',
    indigo: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-700 text-indigo-900 dark:text-indigo-100'
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm animate-pulse">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16 mb-2"></div>
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
      </div>
    );
  }

  return (
    <div 
      className={`
        ${colorClasses[color]} 
        p-4 rounded-lg border shadow-sm 
        hover:shadow-md transition-all duration-200
        ${onClick ? 'cursor-pointer hover:scale-[1.01]' : ''}
      `}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            {icon && <span className="text-xl">{icon}</span>}
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</h3>
          </div>
          
          <div className="flex items-baseline space-x-1">
            <span className="text-2xl font-bold">{typeof value === 'number' ? value.toLocaleString() : value}</span>
            {unit && <span className="text-base text-gray-500 dark:text-gray-400">{unit}</span>}
          </div>
          
          {trend && trend !== 'neutral' && trendValue !== undefined && (
            <div className={`flex items-center space-x-1 mt-2 text-sm ${
              trend === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
            }`}>
              <span>{trend === 'up' ? '↗️' : '↘️'}</span>
              <span>{Math.abs(trendValue)}%</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};